import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, CheckCircle, AlertTriangle, Clock, ArrowUpRight, RefreshCw,
  HardDrive, Users, Rocket, FileCheck, PieChart, TrendingUp, Activity,
  Box, Layers, Shield, ChevronRight, Zap, BarChart3, FileBox
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AnimatedCounter } from "@/components/ui/animated-counter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from 'axios';
import { useAuth, API } from "@/App";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import TestInsights from "./TestInsights";
import DeploymentInsights from "./DeploymentInsights";
import RationalisationProgress from "./RationalisationProgress";

// Chart colors
const COLORS = {
  cyan: '#00D4E4',
  lime: '#BBFF71',
  coral: '#FC5442',
  blue: '#6376F0',
  purple: '#8B5CF6',
  amber: '#F59E0B',
  emerald: '#10B981',
  rose: '#F43F5E',
};

// KPI Card Component
const KPICard = ({ label, value, subtitle, icon: Icon, accentColor = COLORS.cyan, trend, trendUp, onClick }) => (
  <Card 
    className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)] hover:bg-[rgba(229,239,250,0.3)] transition-all duration-300 ${onClick ? 'cursor-pointer' : ''}`}
    onClick={onClick}
    data-testid={`kpi-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardContent className="p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium uppercase tracking-wider text-white/90">{label}</span>
        {Icon && (
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accentColor}20` }}>
            <Icon className="w-6 h-6" style={{ color: accentColor }} />
          </div>
        )}
      </div>
      <div className="flex items-end gap-2">
        <div className="text-4xl font-bold text-white">
          <AnimatedCounter value={value} />
        </div>
        {trend && (
          <div className={`flex items-center text-sm ${trendUp ? 'text-emerald-400' : 'text-rose-400'}`}>
            <ArrowUpRight className={`w-4 h-4 ${!trendUp ? 'rotate-180' : ''}`} />
            {trend}
          </div>
        )}
      </div>
      {subtitle && <div className="text-sm text-white/90 mt-1">{subtitle}</div>}
      {onClick && (
        <div className="mt-2 text-xs text-white/70 flex items-center gap-1">
          Click to view <ChevronRight className="w-3 h-3" />
        </div>
      )}
    </CardContent>
  </Card>
);

// Donut Chart Component
const DonutChart = ({ data, size = 160, strokeWidth = 20 }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  
  let offset = 0;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {data.map((item, i) => {
          const percentage = total > 0 ? item.value / total : 0;
          const strokeDasharray = `${percentage * circumference} ${circumference}`;
          const currentOffset = offset;
          offset += percentage * circumference;
          
          return (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={item.color}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              strokeDashoffset={-currentOffset}
              className="transition-all duration-500"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-white">{total}</span>
        <span className="text-xs text-white/90">Total</span>
      </div>
    </div>
  );
};

// Progress Bar Component
const ProgressBar = ({ value, max, color, label }) => {
  const percentage = max > 0 ? Math.round((value / max) * 100) : 0;
  
  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-white">{label}</span>
        <span className="text-white font-medium">{value}</span>
      </div>
      <div className="h-2 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all duration-500" 
          style={{ width: `${percentage}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    completed: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Completed' },
    failed: { bg: 'bg-rose-500/20', text: 'text-rose-400', label: 'Failed' },
    pending: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Pending' },
    test: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Testing' },
    approved: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Approved' },
    live: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Live' },
    not_deployed: { bg: 'bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px]', text: 'text-white/90', label: 'Not Deployed' },
  };
  
  const c = config[status] || config.pending;
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

// Main Dashboard Component
const AppsDashboard = () => {
  const navigate = useNavigate();
  const { getAccessToken, demoMode } = useAuth();
  const [stats, setStats] = useState(null);
  const [rationalisedFamilies, setRationalisedFamilies] = useState(0);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = async () => {
    if (demoMode) return {};
    try {
      const token = await getAccessToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (error) {
      console.error("Failed to get access token:", error);
      return {};
    }
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      // Fetch both dashboard stats and rationalization summary in parallel
      const [statsRes, rationalRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/discover/rationalization-summary`, { headers })
      ]);
      setStats(statsRes.data);
      setRationalisedFamilies(rationalRes.data.total_families || 0);
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);

  if (loading || !stats) {
    return (
      <div className="p-8" data-testid="apps-dashboard">
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 rounded-full border-4 border-[rgba(229,239,250,0.1)] border-t-cyan-400 animate-spin mb-4" />
          <span className="text-white/90 text-lg">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  const { overview, deployment_stages, installer_types, approvals, phased_deployments, automation_opportunity, recent_activity } = stats;

  // Prepare chart data
  const deploymentStageData = [
    { label: 'Live', value: deployment_stages.live, color: COLORS.emerald },
    { label: 'Approved', value: deployment_stages.approved, color: COLORS.purple },
    { label: 'Testing', value: deployment_stages.test, color: COLORS.blue },
    { label: 'Not Deployed', value: deployment_stages.not_deployed, color: '#4B5563' },
  ];

  const installerTypeData = [
    { label: 'MSI', value: installer_types.msi, color: COLORS.cyan },
    { label: 'EXE', value: installer_types.exe, color: COLORS.amber },
    { label: 'Other', value: installer_types.other, color: COLORS.purple },
  ];

  return (
    <div className="p-8" data-testid="apps-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Package className="h-8 w-8 text-cyan-400" />
            Apps Management Dashboard
          </h1>
          <p className="text-white/90">Overview of packaged applications and deployment status</p>
        </div>
        <Button onClick={loadStats} className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold" data-testid="refresh-dashboard-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="alice-kpi-row grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <KPICard 
          label="Total Packages" 
          value={overview.total_packages} 
          subtitle={`${overview.success_rate}% success rate`}
          icon={Package} 
          accentColor={COLORS.cyan}
          onClick={() => navigate('/packages')}
        />
        <KPICard 
          label="Deployed Live" 
          value={deployment_stages.live} 
          subtitle={`${phased_deployments.active} active rollouts`}
          icon={Rocket} 
          accentColor={COLORS.emerald}
          onClick={() => navigate('/deployments')}
        />
        <KPICard 
          label="Awaiting Approval" 
          value={approvals.awaiting_approval} 
          subtitle={`${approvals.ready_for_live} ready for live`}
          icon={Clock} 
          accentColor={COLORS.amber}
        />
        <KPICard 
          label="Ready to Automate" 
          value={automation_opportunity?.total_with_automation || 0} 
          subtitle={`${rationalisedFamilies} families after rationalisation`}
          icon={Zap} 
          accentColor={COLORS.purple}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Deployment Stage Chart */}
        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <Layers className="w-5 h-5 text-cyan-400" />
              Deployment Stages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <DonutChart data={deploymentStageData} />
              <div className="space-y-3 ml-6">
                {deploymentStageData.map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-white text-sm">{item.label}</span>
                    <span className="text-white font-medium ml-auto">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Installer Types Chart */}
        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <FileBox className="w-5 h-5 text-amber-400" />
              Installer Types
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <DonutChart data={installerTypeData} />
              <div className="space-y-3 ml-6">
                {installerTypeData.map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-white text-sm">{item.label}</span>
                    <span className="text-white font-medium ml-auto">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Package Status */}
        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <Activity className="w-5 h-5 text-emerald-400" />
              Package Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ProgressBar 
              label="Completed" 
              value={overview.completed} 
              max={overview.total_packages} 
              color={COLORS.emerald} 
            />
            <ProgressBar 
              label="Failed" 
              value={overview.failed} 
              max={overview.total_packages} 
              color={COLORS.coral} 
            />
            <ProgressBar 
              label="Pending" 
              value={overview.pending} 
              max={overview.total_packages} 
              color={COLORS.amber} 
            />
            <div className="pt-2 border-t border-[rgba(229,239,250,0.1)]">
              <div className="flex justify-between text-sm">
                <span className="text-white/90">Success Rate</span>
                <span className="text-emerald-400 font-bold">{overview.success_rate}%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Approval Pipeline */}
        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <Shield className="w-5 h-5 text-purple-400" />
              Approval Pipeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="p-4 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-xl text-center cursor-pointer hover:bg-white/[0.04] transition-colors" data-testid="awaiting-approval-card">
                      <div className="text-3xl font-bold text-amber-400">{approvals.awaiting_approval}</div>
                      <div className="text-sm text-white/90 mt-1">Awaiting Approval</div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-gray-900 border-gray-700 p-3 max-w-xs">
                    {approvals.awaiting_approval_apps && approvals.awaiting_approval_apps.length > 0 ? (
                      <div className="space-y-2">
                        <div className="text-xs text-amber-400 font-medium mb-2">Apps Awaiting Approval:</div>
                        {approvals.awaiting_approval_apps.slice(0, 5).map((app, i) => (
                          <div key={i} className="text-sm text-white flex items-center gap-2">
                            <Package className="w-3 h-3 text-amber-400" />
                            {app.name} <span className="text-white/80">v{app.version}</span>
                          </div>
                        ))}
                        {approvals.awaiting_approval_apps.length > 5 && (
                          <div className="text-xs text-white/80">+{approvals.awaiting_approval_apps.length - 5} more</div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-white/90">No apps awaiting approval</div>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="p-4 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-xl text-center cursor-pointer hover:bg-white/[0.04] transition-colors" data-testid="ready-for-live-card">
                      <div className="text-3xl font-bold text-emerald-400">{approvals.ready_for_live}</div>
                      <div className="text-sm text-white/90 mt-1">Ready for Live</div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-gray-900 border-gray-700 p-3 max-w-xs">
                    {approvals.ready_for_live_apps && approvals.ready_for_live_apps.length > 0 ? (
                      <div className="space-y-2">
                        <div className="text-xs text-emerald-400 font-medium mb-2">Apps Ready for Live:</div>
                        {approvals.ready_for_live_apps.slice(0, 5).map((app, i) => (
                          <div key={i} className="text-sm text-white flex items-center gap-2">
                            <CheckCircle className="w-3 h-3 text-emerald-400" />
                            {app.name} <span className="text-white/80">v{app.version}</span>
                          </div>
                        ))}
                        {approvals.ready_for_live_apps.length > 5 && (
                          <div className="text-xs text-white/80">+{approvals.ready_for_live_apps.length - 5} more</div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-white/90">No apps ready for live</div>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <span className="text-white">Total Approvals</span>
                </div>
                <span className="text-white font-medium">{approvals.total_approvals}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-rose-400" />
                  <span className="text-white">Total Rejections</span>
                </div>
                <span className="text-white font-medium">{approvals.total_rejections}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-cyan-400" />
                  <span className="text-white">Phased Rollouts</span>
                </div>
                <span className="text-white font-medium">{phased_deployments.active} active / {phased_deployments.completed} done</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5 text-cyan-400" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recent_activity && recent_activity.length > 0 ? (
              <div className="space-y-3">
                {recent_activity.slice(0, 6).map((pkg, i) => (
                  <div 
                    key={pkg.id || i} 
                    className="flex items-center justify-between p-3 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg hover:bg-white/[0.04] transition-colors cursor-pointer"
                    onClick={() => navigate(`/discover/governance/${encodeURIComponent(pkg.name)}?from=apps`)}
                    data-testid={`recent-activity-${i}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                        <Package className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <div className="text-white font-medium">{pkg.name}</div>
                        <div className="text-white/80 text-sm">v{pkg.version}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={pkg.stage} />
                      <ChevronRight className="w-4 h-4 text-white/70" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/90">No recent activity</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Apps Catalog Info */}
      {stats.winget_catalog_size > 0 && (
        <div className="mt-6 p-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-xl border border-cyan-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-cyan-400" />
              <span className="text-white">Apps Catalog</span>
            </div>
            <span className="text-cyan-400 font-medium">{stats.winget_catalog_size.toLocaleString()} apps available</span>
          </div>
        </div>
      )}

      {/* Rationalisation Progress -- in scope vs excluded, version debt trend,
          version control for managed vs unmanaged apps. Dashboard only. */}
      <RationalisationProgress />

      {/* Deployment Insights -- KPI strip, 30-day trend, failure reasons, top offenders */}
      <DeploymentInsights />

      {/* Test Insights -- deterministic aggregates over tester responses */}
      <TestInsights />
    </div>
  );
};

export default AppsDashboard;
